import React, {ChangeEvent, CSSProperties, Dispatch, SetStateAction, useState} from 'react';
import ReactMde from 'react-mde';
import 'react-mde/lib/styles/css/react-mde-all.css';
import * as Showdown from 'showdown';
import { ChangelogArticle } from '@lfp-digital/back-types-mpg';
import Dropzone from 'react-dropzone';
import { useDispatch } from 'react-redux';
import { InputLabelEditor } from '../../../../components/InputLabelEditor';
import { Separator } from '../Separator';
import { postImageUpload } from '../../../../components/NotificationEditor/components/AvatarSelect';
import { appRoutesToRedirect } from '../../../../components/NotificationEditor/components/NotificationEditorContent/constant';

const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
});

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
    overflow: 'auto',
    height: 'calc(100vh - 300px)',
  },
  inputContainer: {
    marginBottom: 16,
  },
  dropZone: {
    width: 360,
    minHeight: 200,
    border: '1px dashed #4054CC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4054CC',
    cursor: 'pointer',
  },
};

export type ArticleEditorContentProps = {
  onChange: (key: string, value: any) => void;
  data: Partial<ChangelogArticle>;
  versions: string[] | undefined;
  version: string | undefined;
  setVersion: Dispatch<SetStateAction<string | undefined>>;
  isCreation?: boolean;
  isMPP?: boolean;
};

export const ArticleEditorContent = ({data, onChange, isMPP}: ArticleEditorContentProps) => {
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState<'write' | 'preview'>(
    'write',
  );

  const [buttonLinkError, setButtonLinkError] = useState<false | string>(false);

  const checkForButtonLinkError = (buttonLink: string | undefined) => {
    if (
      buttonLink &&
      buttonLink?.indexOf('http') === -1 &&
      buttonLink?.indexOf(':') !== -1
    ) {
      setButtonLinkError(buttonLink.split(':')[1].split('/')[0]);
    }
  };

  const handleAddTag = (newTag: string) => {
    newTag = newTag.replace(/^\s+|\s+$/g, '');
    if (newTag && !data?.tags?.includes(newTag)) {
      onChange('tags', [...(data?.tags || []), newTag]);
    }
  };

  const handleRemoveTag = (tag: string) =>
    onChange('tags', data?.tags?.filter((t) => t !== tag));

  const [inputTag, setInputTag] = useState<string>(" ");

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputTag(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (inputTag.trim() !== "") {
        handleAddTag(inputTag);
        setInputTag("");
      }
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.inputContainer}>
        <InputLabelEditor title="TITLE" isRequired />
        <input
          className="form-control"
          value={data?.title || ''}
          onChange={e => onChange('title', e.target.value)}
        />
      </div>
      <Separator />
      <div style={styles.inputContainer}>
        <InputLabelEditor title="IMAGE" />
        <div style={{display: 'flex', flexDirection: 'row'}}>
          <Dropzone
            style={styles.dropZone}
            onDrop={acceptedFiles => {
              const onSuccess = (imageUrl: string) => {
                onChange('imageUrl', imageUrl);
              };
              dispatch(postImageUpload(acceptedFiles[0], onSuccess));
            }}>
            <div>
              {!data?.imageUrl ? (
                <span>Parcourir ou Drag n Drop</span>
              ) : (
                <img
                  src={data.imageUrl}
                  style={{maxWidth: 360}}
                  alt=""
                />
              )}
            </div>
          </Dropzone>
          {data?.imageUrl && (
            <div
              onClick={() =>
                onChange('imageUrl', undefined)
              }
              style={{
                marginLeft: 16,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 30,
                height: 30,
                backgroundColor: 'red',
                borderRadius: 30,
                cursor: 'pointer',
              }}>
              <i style={{color: 'white'}} className="fa fa-trash" />
            </div>
          )}
        </div>
      </div>
      <Separator />
      <div style={styles.inputContainer}>
        <InputLabelEditor title="TEXT" isRequired />
        <ReactMde
          toolbarCommands={[
            ['bold', 'italic', 'strikethrough'],
            ['unordered-list', 'ordered-list'],
            ['link'],
          ]}
          value={data?.text || ''}
          onChange={value =>
            onChange('text', value)
          }
          selectedTab={selectedTab}
          onTabChange={setSelectedTab}
          generateMarkdownPreview={markdown =>
            Promise.resolve(converter.makeHtml(markdown))
          }
        />
      </div>
      <Separator />
      <div style={styles.inputContainer}>
        <InputLabelEditor title="TAGS" />
        <input
          name="keyword_tags"
          type="text"
          className="form-control"
          onKeyDown={handleKeyPress}
          onChange={handleInputChange}
          value={inputTag}
        />
        <div className="flex flex-row flex-wrap gap-3 mt-4">
          {data?.tags?.map((tag: string, index: number) => (
            <span
              key={`${index}-${tag}`}
              className="inline-flex items-start justify-start px-3 py-2 rounded-[32px] text-sm shadow-sm font-medium bg-blue-100 text-blue-800 mr-2"
            >
              {tag}
              <button
                className="ml-2 hover:text-blue-500"
                onClick={() => handleRemoveTag(tag)}
                title={`Remove ${tag}`}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      </div>
      <Separator />
      <div style={styles.inputContainer}>
        <InputLabelEditor title="CTA" />
        <input
          style={{marginBottom: 8}}
          className="form-control"
          value={data?.ctaText || ''}
          onChange={e =>
            onChange('ctaText', e.target.value)
          }
        />
        <div style={{display: 'flex', flexDirection: 'row'}}>
          <div style={{flex: 1, marginRight: 8}}>
            <select
              value={data?.ctaUrl || ''}
              className="form-control"
              onChange={e => {
                checkForButtonLinkError(e.target.value);
                onChange('ctaUrl', e.target.value);
              }}>
              <option value="">Select screen</option>
              {Object.values(isMPP ? {} : appRoutesToRedirect).map(route => (
                <option key={route.screenName} value={route.path}>
                  {route.screenName}
                </option>
              ))}
            </select>
          </div>
          <div style={{flex: 1, marginLeft: 8}}>
            <div className={`form-group ${buttonLinkError && 'has-error'}`}>
              <input
                onBlur={() =>
                  checkForButtonLinkError(data?.ctaUrl)
                }
                className="form-control"
                value={data?.ctaUrl || ''}
                onChange={e => {
                  onChange('ctaUrl', e.target.value);
                  setButtonLinkError(false);
                }}
              />
              {buttonLinkError && (
                <p
                  style={{
                    color: 'red',
                  }}>
                  You need to specify
                  <b> {buttonLinkError} </b>
                  in CTA redirection
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/pages/Articles/Editor/Content/index.tsx